import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { ClockIcon, LocationIcon, FieldIcon } from "./common/Icons.js";
import { fetchData } from "../utils/api.js";
import {
  formatDateLabel,
  formatTimeLabel,
  formatFieldLabel,
  isGameCanceled,
  buildAppleMapsUrl,
} from "../utils/scheduleHelpers.js";
import { Button } from "./ui/Button";
import "./LeagueGames.css";

const fetchLocationsAndFields = async (leagueId) => {
  try {
    const locs = await fetchData(`/locations?league_id=eq.${leagueId}`).catch(
      () => [],
    );
    const locList = Array.isArray(locs) ? locs : [];
    const fields = await fetchData("/fields").catch(() => []);
    const fieldsList = Array.isArray(fields) ? fields : [];

    const fieldMap = {};
    for (const f of fieldsList) {
      if (f.location_id) {
        if (!fieldMap[f.location_id]) fieldMap[f.location_id] = [];
        fieldMap[f.location_id].push(f);
      }
    }
    return { locList, fieldMap };
  } catch (err) {
    console.error("Failed to load locations/fields:", err);
    return { locList: [], fieldMap: {} };
  }
};

export const LeagueGames = ({ league, selectedSeason, isLeagueAdmin }) => {
  const [games, setGames] = useState([]);
  const [teamsMap, setTeamsMap] = useState({});
  const [locationsMap, setLocationsMap] = useState({});
  const [locationsList, setLocationsList] = useState([]);
  const [fieldsByLoc, setFieldsByLoc] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Dialog states
  const [scoringGame, setScoringGame] = useState(null);
  const [homeScoreInput, setHomeScoreInput] = useState("");
  const [awayScoreInput, setAwayScoreInput] = useState("");
  const [isSavingScore, setIsSavingScore] = useState(false);

  const [reschedulingGame, setReschedulingGame] = useState(null);
  const [newDateTimeInput, setNewDateTimeInput] = useState("");
  const [newLocationId, setNewLocationId] = useState("");
  const [newFieldId, setNewFieldId] = useState("");
  const [isSavingReschedule, setIsSavingReschedule] = useState(false);

  const [cancelingGame, setCancelingGame] = useState(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [actionError, setActionError] = useState("");

  const seasonId = selectedSeason?.season_id || selectedSeason?.id;
  const leagueId = league?.league_id || league?.id;

  const loadData = useCallback(async () => {
    if (!leagueId) return;
    setIsLoading(true);
    try {
      const seasonFilter = seasonId ? `&season_id=eq.${seasonId}` : "";
      const rawGames = await fetchData(
        `/games?league_id=eq.${leagueId}${seasonFilter}`,
      ).catch(() => []);
      const gamesList = Array.isArray(rawGames) ? rawGames : [];

      // Sort games chronologically
      gamesList.sort((a, b) => {
        const tA = new Date(a.start_time || 0).getTime();
        const tB = new Date(b.start_time || 0).getTime();
        return tA - tB;
      });
      setGames(gamesList);

      // Teams
      const teamsRes = await fetchData(`/teams?league_id=eq.${leagueId}`).catch(
        () => [],
      );
      const tMap = {};
      if (Array.isArray(teamsRes)) {
        for (const t of teamsRes) {
          const tid = t.team_id || t.id;
          tMap[tid] = t.team_name || t.name;
        }
      }
      setTeamsMap(tMap);

      // Locations and fields
      const { locList, fieldMap } = await fetchLocationsAndFields(leagueId);
      setLocationsList(locList);
      setFieldsByLoc(fieldMap);
      const lMap = {};
      for (const loc of locList) {
        const lid = loc.location_id || loc.id;
        lMap[lid] = loc;
      }
      setLocationsMap(lMap);
    } catch (err) {
      console.error("Failed to load league games:", err);
    } finally {
      setIsLoading(false);
    }
  }, [leagueId, seasonId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Handle Score Submit
  const handleScoreSubmit = async (e) => {
    e.preventDefault();
    if (!scoringGame) return;
    setIsSavingScore(true);
    setActionError("");
    try {
      const gid = scoringGame.game_id || scoringGame.id;
      const parsedHome = Number.parseInt(homeScoreInput, 10);
      const parsedAway = Number.parseInt(awayScoreInput, 10);
      await fetchData(`/games?game_id=eq.${gid}`, {
        method: "PATCH",
        body: JSON.stringify({
          home_score: Number.isNaN(parsedHome) ? null : parsedHome,
          away_score: Number.isNaN(parsedAway) ? null : parsedAway,
          status: "completed",
        }),
      });
      setScoringGame(null);
      await loadData();
    } catch (err) {
      setActionError(err?.message || "Failed to save scores.");
    } finally {
      setIsSavingScore(false);
    }
  };

  // Handle Reschedule Submit
  const handleRescheduleSubmit = async (e) => {
    e.preventDefault();
    if (!reschedulingGame || !newDateTimeInput) return;
    setIsSavingReschedule(true);
    setActionError("");
    try {
      const gid = reschedulingGame.game_id || reschedulingGame.id;
      const isoStart = new Date(newDateTimeInput).toISOString();
      await fetchData(`/games/${gid}/reschedule`, {
        method: "POST",
        body: JSON.stringify({
          start_time: isoStart,
          location_id: newLocationId || null,
          field_id: newFieldId || null,
        }),
      });
      setReschedulingGame(null);
      await loadData();
    } catch (err) {
      setActionError(err?.message || "Failed to reschedule game.");
    } finally {
      setIsSavingReschedule(false);
    }
  };

  // Handle Cancel Submit
  const handleCancelSubmit = async () => {
    if (!cancelingGame) return;
    setIsCanceling(true);
    setActionError("");
    try {
      const gid = cancelingGame.game_id || cancelingGame.id;
      await fetchData(`/games/${gid}/cancel`, {
        method: "POST",
      });
      setCancelingGame(null);
      await loadData();
    } catch (err) {
      setActionError(err?.message || "Failed to cancel game.");
    } finally {
      setIsCanceling(false);
    }
  };

  if (isLoading) {
    return <div className="loading-spinner" />;
  }

  return (
    <div className="league-games-container">
      {games.length === 0 ? (
        <div className="empty-state">
          <p>No games scheduled for this season yet.</p>
        </div>
      ) : (
        <div className="league-games-list">
          {games.map((game, idx) => {
            const gid = game.game_id || game.id || idx;
            const canceled = isGameCanceled(game);
            const isRescheduled = game.status === "rescheduled";

            const homeTeamName = teamsMap[game.home_team_id] || "Home Team";
            const awayTeamName = teamsMap[game.away_team_id] || "Away Team";

            const locObj = locationsMap[game.location_id];
            const locationName =
              game.location_name || locObj?.name || "Field Location";
            const locationAddress =
              game.location_address || locObj?.address || "";
            const appleMapsUrl = buildAppleMapsUrl(
              locationName,
              locationAddress,
            );
            const fieldName = formatFieldLabel(
              game.field_name || game.field_id,
            );

            // Strikethrough logic
            const dateChanged = Boolean(
              game.original_start_time &&
              game.original_start_time !== game.start_time,
            );
            const origLocObj = locationsMap[game.original_location_id];
            const origLocName =
              game.original_location_name || origLocObj?.name || "";
            const locationChanged = Boolean(
              origLocName && origLocName !== locationName,
            );
            const origFieldRaw =
              game.original_field_name || game.original_field_id;
            const origFieldName = origFieldRaw
              ? formatFieldLabel(origFieldRaw)
              : "";
            const fieldChanged =
              locationChanged ||
              Boolean(origFieldName && origFieldName !== fieldName);

            const hasScores =
              game.home_score !== null &&
              game.home_score !== undefined &&
              game.away_score !== null &&
              game.away_score !== undefined;

            return (
              <div
                key={`league-game-${gid}`}
                className={`league-game-row ${canceled ? "game-canceled" : ""}`}
              >
                <div className="league-game-meta">
                  <span className="league-game-meta-item">
                    <ClockIcon size="xs" />
                    <span className="league-game-meta-stack">
                      <span className="game-datetime-label">
                        {formatDateLabel(game.start_time)}{" "}
                        {formatTimeLabel(game.start_time)}
                      </span>
                      {dateChanged && (
                        <span className="game-meta-struck">
                          {formatDateLabel(game.original_start_time)}{" "}
                          {formatTimeLabel(game.original_start_time)}
                        </span>
                      )}
                    </span>
                    {canceled && (
                      <span className="game-canceled-badge">Canceled</span>
                    )}
                    {isRescheduled && (
                      <span className="game-rescheduled-badge">
                        Rescheduled
                      </span>
                    )}
                  </span>

                  <span className="league-game-meta-item">
                    <LocationIcon size="xs" />
                    <span className="league-game-meta-stack">
                      {appleMapsUrl ? (
                        <a
                          href={appleMapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="wireframe-venue-link"
                        >
                          {locationName}
                        </a>
                      ) : (
                        <span>{locationName}</span>
                      )}
                      {locationChanged && (
                        <span className="game-meta-struck">{origLocName}</span>
                      )}
                    </span>
                  </span>

                  {(fieldName || (fieldChanged && origFieldName)) && (
                    <span className="league-game-meta-item">
                      <FieldIcon size="xs" />
                      <span className="league-game-meta-stack">
                        {fieldName && <span>{fieldName}</span>}
                        {fieldChanged && origFieldName && (
                          <span className="game-meta-struck">
                            {origFieldName}
                          </span>
                        )}
                      </span>
                    </span>
                  )}
                </div>

                <div className="league-game-body">
                  <div className="league-game-teams">
                    <span className="league-team-name">{awayTeamName}</span>
                    <span className="league-team-name">{homeTeamName}</span>
                  </div>

                  {hasScores && (
                    <div className="league-game-scores">
                      <span className="league-score-val">
                        {game.away_score}
                      </span>
                      <span className="league-score-val">
                        {game.home_score}
                      </span>
                    </div>
                  )}

                  {isLeagueAdmin && (
                    <div className="league-game-admin-actions">
                      <button
                        type="button"
                        className="league-admin-btn score-btn"
                        onClick={() => {
                          setScoringGame(game);
                          setHomeScoreInput(game.home_score ?? "");
                          setAwayScoreInput(game.away_score ?? "");
                          setActionError("");
                        }}
                      >
                        Score
                      </button>

                      <button
                        type="button"
                        className="league-admin-btn reschedule-btn"
                        onClick={() => {
                          setReschedulingGame(game);
                          setNewDateTimeInput(
                            game.start_time
                              ? new Date(game.start_time)
                                  .toISOString()
                                  .slice(0, 16)
                              : "",
                          );
                          setNewLocationId(game.location_id || "");
                          setNewFieldId(game.field_id || "");
                          setActionError("");
                        }}
                      >
                        Reschedule
                      </button>

                      {!canceled && (
                        <button
                          type="button"
                          className="league-admin-btn cancel-btn"
                          onClick={() => {
                            setCancelingGame(game);
                            setActionError("");
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Score Reporting Dialog */}
      {scoringGame && (
        <>
          <div
            className="modal-confirm-overlay"
            onClick={() => setScoringGame(null)}
            aria-hidden="true"
          />
          <dialog
            open
            className="modal-confirm league-game-modal"
            aria-labelledby="score-game-title"
          >
            <h3 id="score-game-title">Report Game Score</h3>
            <p className="modal-confirm-subtitle">
              {teamsMap[scoringGame.away_team_id] || "Away"} vs{" "}
              {teamsMap[scoringGame.home_team_id] || "Home"}
            </p>

            {actionError && (
              <div className="registration-error-banner" role="alert">
                {actionError}
              </div>
            )}

            <form onSubmit={handleScoreSubmit} className="league-game-form">
              <div className="form-group">
                <label htmlFor="away-score-input">
                  {teamsMap[scoringGame.away_team_id] || "Away"} Score
                </label>
                <input
                  id="away-score-input"
                  type="number"
                  min="0"
                  required
                  value={awayScoreInput}
                  onChange={(e) => setAwayScoreInput(e.target.value)}
                  className="game-score-field"
                />
              </div>

              <div className="form-group">
                <label htmlFor="home-score-input">
                  {teamsMap[scoringGame.home_team_id] || "Home"} Score
                </label>
                <input
                  id="home-score-input"
                  type="number"
                  min="0"
                  required
                  value={homeScoreInput}
                  onChange={(e) => setHomeScoreInput(e.target.value)}
                  className="game-score-field"
                />
              </div>

              <div className="modal-confirm-actions">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setScoringGame(null)}
                  disabled={isSavingScore}
                >
                  Close
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSavingScore}
                >
                  {isSavingScore ? "Saving..." : "Save Score"}
                </Button>
              </div>
            </form>
          </dialog>
        </>
      )}

      {/* Reschedule Dialog */}
      {reschedulingGame && (
        <>
          <div
            className="modal-confirm-overlay"
            onClick={() => setReschedulingGame(null)}
            aria-hidden="true"
          />
          <dialog
            open
            className="modal-confirm league-game-modal"
            aria-labelledby="reschedule-game-title"
          >
            <h3 id="reschedule-game-title">Reschedule Game</h3>
            <p className="modal-confirm-subtitle">
              {teamsMap[reschedulingGame.away_team_id] || "Away"} vs{" "}
              {teamsMap[reschedulingGame.home_team_id] || "Home"}
            </p>

            {actionError && (
              <div className="registration-error-banner" role="alert">
                {actionError}
              </div>
            )}

            <form
              onSubmit={handleRescheduleSubmit}
              className="league-game-form"
            >
              <div className="form-group">
                <label htmlFor="new-datetime-input">New Date & Time</label>
                <input
                  id="new-datetime-input"
                  type="datetime-local"
                  required
                  value={newDateTimeInput}
                  onChange={(e) => setNewDateTimeInput(e.target.value)}
                  className="game-score-field"
                />
              </div>

              <div className="form-group">
                <label htmlFor="new-loc-select">Location</label>
                <select
                  id="new-loc-select"
                  value={newLocationId}
                  onChange={(e) => {
                    setNewLocationId(e.target.value);
                    setNewFieldId("");
                  }}
                  className="game-select-field"
                >
                  <option value="">Select Location</option>
                  {locationsList.map((loc) => {
                    const lid = loc.location_id || loc.id;
                    return (
                      <option key={lid} value={lid}>
                        {loc.name}
                      </option>
                    );
                  })}
                </select>
              </div>

              {newLocationId && fieldsByLoc[newLocationId]?.length > 0 && (
                <div className="form-group">
                  <label htmlFor="new-field-select">Field</label>
                  <select
                    id="new-field-select"
                    value={newFieldId}
                    onChange={(e) => setNewFieldId(e.target.value)}
                    className="game-select-field"
                  >
                    <option value="">Select Field</option>
                    {fieldsByLoc[newLocationId].map((f) => (
                      <option key={f.field_id} value={f.field_id}>
                        {f.field_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="modal-confirm-actions">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setReschedulingGame(null)}
                  disabled={isSavingReschedule}
                >
                  Close
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSavingReschedule}
                >
                  {isSavingReschedule ? "Rescheduling..." : "Reschedule Game"}
                </Button>
              </div>
            </form>
          </dialog>
        </>
      )}

      {/* Cancel Confirmation Dialog */}
      {cancelingGame && (
        <>
          <div
            className="modal-confirm-overlay"
            onClick={() => setCancelingGame(null)}
            aria-hidden="true"
          />
          <dialog
            open
            className="modal-confirm league-game-modal"
            aria-labelledby="cancel-game-title"
          >
            <h3 id="cancel-game-title">Cancel Game</h3>
            <p className="modal-confirm-subtitle">
              Are you sure you want to cancel this game between{" "}
              {teamsMap[cancelingGame.away_team_id] || "Away"} and{" "}
              {teamsMap[cancelingGame.home_team_id] || "Home"}?
            </p>

            {actionError && (
              <div className="registration-error-banner" role="alert">
                {actionError}
              </div>
            )}

            <div className="modal-confirm-actions">
              <Button
                variant="outline"
                type="button"
                onClick={() => setCancelingGame(null)}
                disabled={isCanceling}
              >
                Keep Game
              </Button>
              <Button
                variant="destructive"
                type="button"
                onClick={handleCancelSubmit}
                disabled={isCanceling}
              >
                {isCanceling ? "Canceling..." : "Cancel Game"}
              </Button>
            </div>
          </dialog>
        </>
      )}
    </div>
  );
};

LeagueGames.propTypes = {
  league: PropTypes.object.isRequired,
  selectedSeason: PropTypes.object,
  isLeagueAdmin: PropTypes.bool,
};

export default LeagueGames;
